// String Art generation engine — runs entirely in the browser.

// Build a single ring of nails around the shape perimeter.
function buildRing(shape, numNails, W, H, margin = 3, scale = 1) {
  const nails = [];
  if (shape === "circle") {
    const cx = W / 2;
    const cy = H / 2;
    const r = (Math.min(W, H) / 2) * scale - margin;
    for (let i = 0; i < numNails; i++) {
      const a = (2 * Math.PI * i) / numNails - Math.PI / 2;
      nails.push({ x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) });
    }
    return nails;
  }
  if (shape === "heart") {
    // Parametric heart curve. y is negated so the heart points up on canvas.
    const cx = W / 2;
    const cy = H / 2;
    const sx = ((W / 2) * scale - margin) / 17;
    const sy = ((H / 2) * scale - margin) / 15;
    const s = Math.min(sx, sy);
    for (let i = 0; i < numNails; i++) {
      const t = (2 * Math.PI * i) / numNails;
      const hx = 16 * Math.pow(Math.sin(t), 3);
      const hy =
        13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
      nails.push({ x: cx + hx * s, y: cy - hy * s + H * 0.04 });
    }
    return nails;
  }
  // rectangle / square perimeter (centred, scaled)
  const mx = (W * (1 - scale)) / 2 + margin;
  const my = (H * (1 - scale)) / 2 + margin;
  const x0 = mx;
  const y0 = my;
  const x1 = W - mx;
  const y1 = H - my;
  const w = x1 - x0;
  const h = y1 - y0;
  const per = 2 * (w + h);
  for (let i = 0; i < numNails; i++) {
    const d = (per * i) / numNails;
    let x;
    let y;
    if (d < w) {
      x = x0 + d;
      y = y0;
    } else if (d < w + h) {
      x = x1;
      y = y0 + (d - w);
    } else if (d < 2 * w + h) {
      x = x1 - (d - w - h);
      y = y1;
    } else {
      x = x0;
      y = y1 - (d - 2 * w - h);
    }
    nails.push({ x, y });
  }
  return nails;
}

// Compute nail positions. When `rings` > 1, concentric inner rings are appended
// (the outer ring is always the first `numNails` entries, used for masking).
export function computeNailPositions(shape, numNails, W, H, margin = 3, scale = 1, rings = 1) {
  const R = Math.max(1, Math.min(3, rings | 0));
  const factors = R === 1 ? [1] : R === 2 ? [1, 0.62] : [1, 0.72, 0.44];
  let nails = [];
  for (const f of factors) {
    const cnt = f === 1 ? numNails : Math.max(24, Math.round(numNails * f));
    nails = nails.concat(buildRing(shape, cnt, W, H, margin, scale * f));
  }
  return nails;
}


// Draw an image into a WxH context with the chosen fit:
//  - "cover":   fill the frame, cropping overflow (default)
//  - "contain": fit the whole image inside, centered (no cropping; may add margins)
export function drawImageFit(ctx, img, W, H, fit = "cover", bg = "#ffffff", scale = 1) {
  if (bg === null) {
    ctx.clearRect(0, 0, W, H);
  } else {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);
  }
  const ir = img.width / img.height;
  const tr = W / H;
  let dw;
  let dh;
  if (fit === "contain") {
    if (ir > tr) {
      dw = W;
      dh = W / ir;
    } else {
      dh = H;
      dw = H * ir;
    }
  } else if (ir > tr) {
    dh = H;
    dw = H * ir;
  } else {
    dw = W;
    dh = W / ir;
  }
  dw *= scale;
  dh *= scale;
  ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
}

// Rasterise the nail polygon into a binary mask (1 = inside the shape).
// Works for any closed shape (circle, heart, ...) instead of a radius test.
export function buildShapeMask(nails, W, H) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.moveTo(nails[0].x, nails[0].y);
  for (let i = 1; i < nails.length; i++) ctx.lineTo(nails[i].x, nails[i].y);
  ctx.closePath();
  ctx.fill();
  const d = ctx.getImageData(0, 0, W, H).data;
  const mask = new Uint8Array(W * H);
  for (let i = 0; i < mask.length; i++) mask[i] = d[i * 4] > 127 ? 1 : 0;
  return mask;
}

// Otsu threshold over a luminance histogram.
function otsuThreshold(hist, total) {
  let sum = 0;
  for (let i = 0; i < 256; i++) sum += i * hist[i];
  let sumB = 0;
  let wB = 0;
  let max = 0;
  let threshold = 128;
  for (let i = 0; i < 256; i++) {
    wB += hist[i];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += i * hist[i];
    const mB = sumB / wB;
    const mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > max) {
      max = between;
      threshold = i;
    }
  }
  return threshold;
}

// Detect the subject silhouette and place nails along its contour by radial
// sampling from the centroid. Uses the alpha channel for transparent PNGs,
// otherwise an Otsu luminance threshold (assumes the subject is the darker
// region). Returns a closed loop ordered by angle (also usable as a mask).
export function computeSilhouetteNails(img, numNails, W, H, fit = "cover", scale = 1) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  drawImageFit(ctx, img, W, H, fit, null, scale); // transparent bg
  const data = ctx.getImageData(0, 0, W, H).data;

  let hasAlpha = false;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 200) {
      hasAlpha = true;
      break;
    }
  }

  const fg = new Uint8Array(W * H);
  if (hasAlpha) {
    for (let p = 0; p < W * H; p++) fg[p] = data[p * 4 + 3] > 40 ? 1 : 0;
  } else {
    const hist = new Array(256).fill(0);
    for (let p = 0; p < W * H; p++) {
      const i = p * 4;
      const lum = Math.round(0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2]);
      hist[lum]++;
    }
    const t = otsuThreshold(hist, W * H);
    for (let p = 0; p < W * H; p++) {
      const i = p * 4;
      const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      fg[p] = lum < t ? 1 : 0; // darker = subject
    }
  }

  let sx = 0;
  let sy = 0;
  let n = 0;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (fg[y * W + x]) {
        sx += x;
        sy += y;
        n++;
      }
    }
  }
  if (n < 50) return computeNailPositions("circle", numNails, W, H, 3, scale);
  const cx = sx / n;
  const cy = sy / n;

  const maxR = Math.hypot(W, H);
  const minR = Math.min(W, H) * 0.08;
  const nails = [];
  for (let i = 0; i < numNails; i++) {
    const a = (2 * Math.PI * i) / numNails - Math.PI / 2;
    const dx = Math.cos(a);
    const dy = Math.sin(a);
    let lastR = 0;
    for (let r = 2; r < maxR; r += 1.5) {
      const xx = Math.round(cx + dx * r);
      const yy = Math.round(cy + dy * r);
      if (xx < 0 || yy < 0 || xx >= W || yy >= H) break;
      if (fg[yy * W + xx]) lastR = r;
    }
    const r = lastR > minR ? lastR - 1 : minR;
    nails.push({ x: cx + dx * r, y: cy + dy * r });
  }
  return nails;
}

// Build an "error" map from an image element. Higher value = darker = needs more thread.
// When `nails` is supplied for a non-rectangular shape, pixels outside the
// nail polygon are masked out (works for circle, heart, etc.).
export function buildErrorMap(img, shape, W, H, invert = false, fit = "cover", scale = 1, nails = null) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  drawImageFit(ctx, img, W, H, fit, "#ffffff", scale);

  const data = ctx.getImageData(0, 0, W, H).data;
  const error = new Float32Array(W * H);
  const useMask = nails && nails.length > 2 && (shape === "circle" || shape === "heart" || shape === "silhouette");
  const mask = useMask ? buildShapeMask(nails, W, H) : null;
  const cx = W / 2;
  const cy = H / 2;
  const r = Math.min(W, H) / 2;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const idx = y * W + x;
      const p = idx * 4;
      let gray = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2];
      let val = invert ? gray : 255 - gray;
      if (mask) {
        if (!mask[idx]) val = 0;
      } else if (shape === "circle") {
        const dist = Math.hypot(x - cx, y - cy);
        if (dist > r) val = 0;
      }
      error[idx] = val;
    }
  }
  return error;
}

function linePixels(a, b, W, H) {
  // Bresenham, returns Int32Array of flat pixel indices
  let x0 = Math.round(a.x);
  let y0 = Math.round(a.y);
  const x1 = Math.round(b.x);
  const y1 = Math.round(b.y);
  const dx = Math.abs(x1 - x0);
  const dy = -Math.abs(y1 - y0);
  const sx = x0 < x1 ? 1 : -1;
  const sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  const out = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (x0 >= 0 && x0 < W && y0 >= 0 && y0 < H) out.push(y0 * W + x0);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x0 += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y0 += sy;
    }
  }
  return Int32Array.from(out);
}

// Greedy line generation. Async, reports progress via onProgress(0..1).
// - minDist: reject chords shorter than this (in pixels) — shape-agnostic,
//   so it works for circle, heart, rectangle and custom layouts.
// - lengthPenalty (0..1): subtracts a fraction of the normalised line length
//   from the score, favouring shorter lines => more local detail, less haze.
export async function generateSequence({
  error,
  nails,
  W,
  H,
  numLines,
  lineWeight = 18,
  minDist,
  lengthPenalty = 0,
  onProgress,
}) {
  const N = nails.length;
  const cache = new Map();
  const getLine = (a, b) => {
    const key = a < b ? a * N + b : b * N + a;
    let px = cache.get(key);
    if (!px) {
      px = linePixels(nails[a], nails[b], W, H);
      cache.set(key, px);
    }
    return px;
  };

  const minD = minDist != null ? minDist : 0.05 * Math.min(W, H);
  const minD2 = minD * minD;
  const maxLen = Math.hypot(W, H);
  let current = 0;
  let prev = -1;
  const sequence = [current];
  const batch = 30;

  for (let line = 0; line < numLines; line++) {
    let best = -1;
    let bestScore = -Infinity;
    let bestPixels = null;
    const cnx = nails[current].x;
    const cny = nails[current].y;
    for (let next = 0; next < N; next++) {
      if (next === current || next === prev) continue;
      const ddx = nails[next].x - cnx;
      const ddy = nails[next].y - cny;
      const dist2 = ddx * ddx + ddy * ddy;
      if (dist2 < minD2) continue;
      const px = getLine(current, next);
      let sum = 0;
      for (let p = 0; p < px.length; p++) sum += error[px[p]];
      let score = sum / px.length;
      if (lengthPenalty > 0) {
        score -= lengthPenalty * (Math.sqrt(dist2) / maxLen) * 255;
      }
      if (score > bestScore) {
        bestScore = score;
        best = next;
        bestPixels = px;
      }
    }
    if (best === -1 || bestScore <= 0) break;
    for (let p = 0; p < bestPixels.length; p++) {
      const i = bestPixels[p];
      error[i] = Math.max(0, error[i] - lineWeight);
    }
    sequence.push(best);
    prev = current;
    current = best;
    if (line % batch === 0) {
      if (onProgress) onProgress(line / numLines);
      // yield to keep UI responsive
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 0));
    }
  }
  if (onProgress) onProgress(1);
  return sequence;
}

// Render a sequence onto a display canvas (each segment stroked individually
// so overlapping threads build up opacity for a realistic look).
export function renderSequence(canvas, sequence, nails, W, H, opts) {
  const { threadColor, bgColor, lineOpacity, count, showNails } = opts;
  const ctx = canvas.getContext("2d");
  const dw = canvas.width;
  const dh = canvas.height;
  const sx = dw / W;
  const sy = dh / H;
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, dw, dh);
  ctx.strokeStyle = threadColor;
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.globalAlpha = lineOpacity;
  const n = count != null ? Math.min(count, sequence.length) : sequence.length;
  for (let i = 1; i < n; i++) {
    const a = nails[sequence[i - 1]];
    const b = nails[sequence[i]];
    ctx.beginPath();
    ctx.moveTo(a.x * sx, a.y * sy);
    ctx.lineTo(b.x * sx, b.y * sy);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  if (showNails) {
    ctx.fillStyle = "#FF3B00";
    for (let i = 0; i < nails.length; i++) {
      ctx.beginPath();
      ctx.arc(nails[i].x * sx, nails[i].y * sy, 1.4, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
}

export function workingDims(shape, baseSize = 640, aspect = 4 / 3) {
  if (shape === "rectangle") {
    return { W: baseSize, H: Math.round(baseSize / aspect) };
  }
  return { W: baseSize, H: baseSize };
}

// ---------------------------------------------------------------------------
// Multi-color string art (color layers)
// ---------------------------------------------------------------------------
function hexToRgb(hex) {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

// Build one error/demand map per palette color.
// blend "multiply" => subtractive inks on white; "lighter" => additive light on black.
export function buildColorErrorMaps(img, palette, blend, shape, W, H, fit = "cover", scale = 1, nails = null) {
  const c = document.createElement("canvas");
  c.width = W;
  c.height = H;
  const ctx = c.getContext("2d");
  drawImageFit(ctx, img, W, H, fit, blend === "lighter" ? "#000000" : "#ffffff", scale);

  const data = ctx.getImageData(0, 0, W, H).data;
  const cx = W / 2;
  const cy = H / 2;
  const r = Math.min(W, H) / 2;
  const useMask = nails && nails.length > 2 && (shape === "circle" || shape === "heart" || shape === "silhouette");
  const shapeMask = useMask ? buildShapeMask(nails, W, H) : null;

  const units = palette.map((hex) => {
    const rgb = hexToRgb(hex);
    const v =
      blend === "lighter"
        ? [rgb.r, rgb.g, rgb.b]
        : [255 - rgb.r, 255 - rgb.g, 255 - rgb.b];
    const len = Math.hypot(v[0], v[1], v[2]) || 1;
    return [v[0] / len, v[1] / len, v[2] / len];
  });

  const maps = palette.map(() => new Float32Array(W * H));
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const idx = y * W + x;
      const p = idx * 4;
      const masked = shapeMask
        ? !shapeMask[idx]
        : shape === "circle" && Math.hypot(x - cx, y - cy) > r;
      const tv =
        blend === "lighter"
          ? [data[p], data[p + 1], data[p + 2]]
          : [255 - data[p], 255 - data[p + 1], 255 - data[p + 2]];
      for (let k = 0; k < units.length; k++) {
        if (masked) {
          maps[k][idx] = 0;
          continue;
        }
        const u = units[k];
        let d = tv[0] * u[0] + tv[1] * u[1] + tv[2] * u[2];
        if (d < 0) d = 0;
        else if (d > 255) d = 255;
        maps[k][idx] = d;
      }
    }
  }
  return maps;
}

export async function generateLayers({
  img,
  shape,
  W,
  H,
  nails,
  numLines,
  lineWeight,
  palette,
  blend,
  fit = "cover",
  scale = 1,
  minDist,
  lengthPenalty = 0,
  onProgress,
}) {
  const maps = buildColorErrorMaps(img, palette, blend, shape, W, H, fit, scale, nails);
  const layers = [];
  for (let i = 0; i < palette.length; i++) {
    // eslint-disable-next-line no-await-in-loop
    const seq = await generateSequence({
      error: maps[i],
      nails,
      W,
      H,
      numLines,
      lineWeight,
      minDist,
      lengthPenalty,
      onProgress: (p) => onProgress && onProgress((i + p) / palette.length),
    });
    layers.push({ color: palette[i], sequence: seq });
  }
  if (onProgress) onProgress(1);
  return layers;
}

// Estimate total thread length (metres) for a sequence given the real frame
// width in centimetres. Used for material/time estimates in the UI.
export function threadLengthMeters(sequence, nails, W, realWidthCm) {
  if (!sequence || sequence.length < 2) return 0;
  let px = 0;
  for (let i = 1; i < sequence.length; i++) {
    const a = nails[sequence[i - 1]];
    const b = nails[sequence[i]];
    px += Math.hypot(a.x - b.x, a.y - b.y);
  }
  const cmPerPx = realWidthCm / W;
  return (px * cmPerPx) / 100;
}

export function layersThreadMeters(layers, nails, W, realWidthCm) {
  return layers.reduce((sum, l) => sum + threadLengthMeters(l.sequence, nails, W, realWidthCm), 0);
}

export function renderLayers(canvas, layers, nails, W, H, opts) {
  const { bgColor, lineOpacity, blend, fraction = 1, showNails } = opts;
  const ctx = canvas.getContext("2d");
  const dw = canvas.width;
  const dh = canvas.height;
  const sx = dw / W;
  const sy = dh / H;
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, dw, dh);
  ctx.lineWidth = 1;
  ctx.lineCap = "round";
  ctx.globalCompositeOperation = blend;
  for (const layer of layers) {
    ctx.strokeStyle = layer.color;
    ctx.globalAlpha = lineOpacity;
    const seq = layer.sequence;
    const n = Math.max(0, Math.min(seq.length, Math.floor(fraction * seq.length)));
    for (let i = 1; i < n; i++) {
      const a = nails[seq[i - 1]];
      const b = nails[seq[i]];
      ctx.beginPath();
      ctx.moveTo(a.x * sx, a.y * sy);
      ctx.lineTo(b.x * sx, b.y * sy);
      ctx.stroke();
    }
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = "source-over";
  if (showNails) {
    ctx.fillStyle = "#FF3B00";
    for (let i = 0; i < nails.length; i++) {
      ctx.beginPath();
      ctx.arc(nails[i].x * sx, nails[i].y * sy, 1.4, 0, 2 * Math.PI);
      ctx.fill();
    }
  }
}
